import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  Checkbox,
  Chip,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { BiSolidCheckCircle } from 'react-icons/bi';
import { buildFeatureTree } from '../utils/featureTreeUtils';
import { filterEligibleFeatures } from '../utils/featureEligibility';
import type { Feature } from '../types';

export interface FeatureTreeSelectorProps {
  features: Feature[];
  selectedIds: Set<number> | number[];
  onToggle?: (featureId: number, checked: boolean) => void;
  mode: 'checkbox' | 'switch';
  disabled?: boolean;
  disabledIds?: Set<number>;
  showDescriptions?: boolean;
  readOnly?: boolean;
  targetUserTiers?: Set<string>;
  /** Service keys enabled on the user's rooftops; hides features whose `config.requires` isn't met. */
  enabledServices?: Set<string>;
}

const noop = () => {};

const FeatureTreeSelector: React.FC<FeatureTreeSelectorProps> = ({
  features,
  selectedIds,
  onToggle = noop,
  mode,
  disabled = false,
  disabledIds,
  showDescriptions = false,
  readOnly = false,
  targetUserTiers,
  enabledServices,
}) => {
  const selectedSet = useMemo(
    () => (selectedIds instanceof Set ? selectedIds : new Set(selectedIds)),
    [selectedIds],
  );

  // Tier + service-requirement gating — single source of truth in featureEligibility.
  const filteredFeatures = useMemo(
    () => filterEligibleFeatures(features, { tiers: targetUserTiers, enabledServices }),
    [features, targetUserTiers, enabledServices],
  );

  const tree = useMemo(() => buildFeatureTree(filteredFeatures), [filteredFeatures]);

  if (tree.length === 0) {
    return <Typography variant="body2" color="text.secondary">No features available</Typography>;
  }

  const isDisabled = (featureId: number) =>
    disabled || (disabledIds?.has(featureId) ?? false);

  // -- Read-only mode --

  if (readOnly) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {tree.map(({ root, children }, groupIdx) => {
          const rootEnabled = selectedSet.has(root.id);
          const enabledChildren = children.filter(c => selectedSet.has(c.id));

          if (!rootEnabled && enabledChildren.length === 0) return null;

          return (
            <React.Fragment key={root.id}>
              {groupIdx > 0 && <Divider />}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <BiSolidCheckCircle size={16} color="#2e7d32" />
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                    {root.label}
                  </Typography>
                </Box>
                {enabledChildren.length > 0 && (
                  <Box sx={{ pl: 3, mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {enabledChildren.map(child => (
                      <Chip
                        key={child.id}
                        label={child.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 500,
                          backgroundColor: 'rgba(59, 130, 246, 0.12)',
                          color: '#1d4ed8',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  // -- Switch mode --

  if (mode === 'switch') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tree.map(({ root, children }, groupIdx) => {
          const rootChecked = selectedSet.has(root.id);
          const enabledCount = children.filter(c => selectedSet.has(c.id)).length;

          return (
            <React.Fragment key={root.id}>
              {groupIdx > 0 && <Divider />}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {root.label}
                      </Typography>
                      {children.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                          {enabledCount} of {children.length} enabled
                        </Typography>
                      )}
                    </Box>
                    {showDescriptions && root.description && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                        {root.description}
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    checked={rootChecked}
                    disabled={isDisabled(root.id)}
                    onChange={(_e, checked) => onToggle(root.id, checked)}
                    size="small"
                  />
                </Box>

                {children.length > 0 && (
                  <Box sx={{ pl: 2.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5, opacity: rootChecked ? 1 : 0.45 }}>
                    {children.map(child => (
                      <Box
                        key={child.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13 }}>{child.label}</Typography>
                          {showDescriptions && child.description && (
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                              {child.description}
                            </Typography>
                          )}
                        </Box>
                        <Switch
                          checked={selectedSet.has(child.id)}
                          disabled={!rootChecked || isDisabled(child.id)}
                          onChange={(_e, checked) => onToggle(child.id, checked)}
                          size="small"
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  // -- Checkbox mode --

  return (
    <Box>
      {tree.map(({ root, children }) => (
        <Box key={root.id}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectedSet.has(root.id)}
                disabled={isDisabled(root.id)}
                onChange={(_e, checked) => onToggle(root.id, checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {root.label}
                </Typography>
                {showDescriptions && root.description && (
                  <Typography variant="caption" color="text.secondary">
                    {root.description}
                  </Typography>
                )}
              </Box>
            }
            sx={{ display: 'flex', m: 0 }}
          />
          {children.map(child => (
            <FormControlLabel
              key={child.id}
              control={
                <Checkbox
                  size="small"
                  checked={selectedSet.has(child.id)}
                  disabled={isDisabled(child.id)}
                  onChange={(_e, checked) => onToggle(child.id, checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{child.label}</Typography>
                  {showDescriptions && child.description && (
                    <Typography variant="caption" color="text.secondary">
                      {child.description}
                    </Typography>
                  )}
                </Box>
              }
              sx={{ display: 'flex', m: 0, pl: 3 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default FeatureTreeSelector;
